import axios from '@axios'
import router from '@/router'
import { checkGenderForProgram, checkProgramRegistrationIsDisableByGender } from '@/helpers/checkGenderForProgram'
import { hasValue } from '@/helpers/formConditions'

export default {
  namespaced: true,
  state: {
    childrenList: [],
    selectedProgramGender: null,
    selectedProgramCheckGender: false,
    selectedProgramAgeRanges: {
      age_range_from: null,
      age_range_to: null,
    },
    selectedProgramDynamicRequirements: [],
    isLoaded: false,
  },
  getters: {
    getChildrenList: state => state.childrenList,
    hasChildrenLoaded: state => state.isLoaded,
  },
  mutations: {
    SET_ALL_CHILDREN: (state, data) => {
      state.childrenList = data
      state.isLoaded = true
    },
    SET_SELECTED_PROGRAM_AVAILABILITY_SETTINGS: (state, data) => {
      state.selectedProgramGender = checkGenderForProgram(data.gender)
      if (data.age_range_from || data.age_range_to) {
        state.selectedProgramCheckGender = true
        state.selectedProgramAgeRanges = {
          age_range_from: data.age_range_from,
          age_range_to: data.age_range_to,
        }
      } else {
        state.selectedProgramCheckGender = false
        state.selectedProgramAgeRanges = {
          age_range_from: null,
          age_range_to: null,
        }
      }

      state.selectedProgramDynamicRequirements = data.dynamic_requirements
    },
  },
  actions: {
    fetchChildrenList(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/my-children', {
            params: queryParams,
          })
          .then(response => {
            ctx.commit('SET_ALL_CHILDREN', response.data.data)
            resolve(response)
          })
          .catch(error => reject(error))
      })
    },
    isProgramDisableByGender({ state }, child) {
      const gender = child.extra_user_info ? child.extra_user_info.gender : ''

      return checkProgramRegistrationIsDisableByGender(gender, state.selectedProgramGender)
    },
    async updateChildrenListWithProgramAccess({ state, dispatch }) {
      state.childrenList = await Promise.all(state.childrenList.map(async child => {
        let isDisabled = false
        let disabledReason = ''
        let ageIsEmpty = false

        const isDisabledByGender = await dispatch('isProgramDisableByGender', child)
        if (isDisabledByGender) {
          isDisabled = true
          disabledReason = `The program is available for ${state.selectedProgramGender.toLowerCase()} only`
        }

        // Check by age
        if (!isDisabled && state.selectedProgramCheckGender) {
          const age = child.extra_user_info?.age ?? null

          if (age === null || age === 0) {
            isDisabled = false
            ageIsEmpty = true
          } else {
            const { age_range_from, age_range_to } = state.selectedProgramAgeRanges

            if (age_range_from && age_range_to) {
              if (age < age_range_from || age > age_range_to) {
                isDisabled = true
                disabledReason = `The program is suitable for ages ${age_range_from} - ${age_range_to}`
              }
            } else if (age_range_from && !age_range_to) {
              if (age < age_range_from) {
                isDisabled = true
                disabledReason = `The program is suitable for ages ${age_range_from} and above`
              }
            } else if (!age_range_from && age_range_to) {
              if (age > age_range_to) {
                isDisabled = true
                disabledReason = `This program is suitable for ages up to ${age_range_to}`
              }
            }
          }
        }

        let dynamicFieldIsEmpty = false
        let emptyDynamicFields = []
        let failedRequirementsLabels = []

        if (state.selectedProgramDynamicRequirements.length > 0) {
          state.selectedProgramDynamicRequirements.forEach(requirement => {
            const dynamicField = child.dynamic_profile_fields.find(field => field.original_id == requirement.id)

            if (!dynamicField || dynamicField.value == null) {
              dynamicFieldIsEmpty = true
              emptyDynamicFields.push({ ...requirement })
            } else {
              const result = hasValue(requirement, dynamicField.value)

              if (!result) {
                failedRequirementsLabels.push(requirement.label)
              }
            }
          })

          if (failedRequirementsLabels.length > 0) {
            isDisabled = true
            disabledReason = `This program is unavailable because it does not meet the requirement: ${failedRequirementsLabels.join(', ')}.`
          }
        }

        return {
          ...child,
          isDisabled,
          disabledReason,
          ageIsEmpty,
          dynamicFieldIsEmpty,
          emptyDynamicFields,
        }
      }))
    },
    goToApplicationPage(ctx, {child, programId}) {
      if (child.applicationsByProgram[programId] === undefined) {
        router.push({
          name: 'application-create',
          params: { id: programId },
          query: { user_id: child.id }
        })
      } else {
        router.push({
          name: 'application-create',
          params: { id: programId },
          query: {
            app_id: child.applicationsByProgram[programId].id,
            user_id: child.id,
          }
        })
      }
    },
  },
}
